// Client for the site's /api/v1 endpoints. The layer modules call it through
// window.noaaGreatLakesApi so tests can replace it.
const API = "/api/v1";

function announceRequest(endpoint, startedAt, response = null, failure = "") {
  document.dispatchEvent(new CustomEvent("great-lakes-api-request", {
    detail: {
      endpoint,
      durationMs: Math.max(0, Math.round(performance.now() - startedAt)),
      status: response?.status || 0,
      success: Boolean(response?.ok) && !failure,
      failure,
      requestId: response?.headers?.get("X-Request-ID") || "",
      online: navigator.onLine !== false,
    },
  }));
}

async function getJson(path, params = {}, { signal, cache: requestCache, url, endpoint = path } = {}) {
  const query = new URLSearchParams(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== "")).toString();
  const startedAt = performance.now();
  let response;
  let failure = "";
  try {
    response = await fetch(url || `${API}${path}${query ? `?${query}` : ""}`, { signal, ...(requestCache ? { cache: requestCache } : {}) });
    if (!response.ok) {
      failure = "http";
      throw new Error(`${path} request failed (${response.status})`);
    }
    try {
      return await response.json();
    } catch (error) {
      failure = error?.name === "SyntaxError" ? "invalid_json" : "response_body";
      throw error;
    }
  } catch (error) {
    failure = signal?.reason?.name === "TimeoutError" ? "timeout" : failure || "network";
    throw error;
  } finally {
    // User navigation and superseded work are expected; timeouts remain failures.
    if (!signal?.aborted || signal.reason?.name === "TimeoutError") announceRequest(endpoint, startedAt, response, failure);
  }
}

export const api = {
  // dataVersion changes when NOAA publishes a run or "Now" advances an hour,
  // so the browser's HTTP cache never serves an older frame. With a ``time``
  // (ISO 8601), the saved surface map of that past sample instead.
  conditions({ layer, forecastHour, depth, targetC, selection, dataVersion = "", time, signal }) {
    if (time) return getJson(`/history/layers/${encodeURIComponent(layer)}`, { time }, { signal });
    const params = { forecastHour, data: dataVersion };
    if (layer === "temperature" || layer === "currents") params.depth = depth;
    if (layer === "target-temperature") Object.assign(params, { targetC, selection, scale: "auto" });
    return getJson(`/layers/${encodeURIComponent(layer)}`, params, { signal });
  },
  // A layer's forecast animation: frame URLs and their shared colour scale,
  // or { ready: false, progress } while the server is still drawing them.
  animation({ layer, depth, signal, refresh = false }) {
    const params = layer === "temperature" || layer === "currents" ? { depth } : {};
    return getJson(`/animation/${encodeURIComponent(layer)}`, params, { signal, cache: refresh ? "no-store" : undefined });
  },
  async animationFrame({ url, signal }) {
    return getJson("/animation/frame", {}, { signal, url });
  },
  // Point readings take a ``time`` too: the saved conditions of that past sample.
  temperatureValue({ forecastHour, depth, latitude, longitude, time, frame, inputs }) {
    return time ? getJson("/history/point/temperature", { time, depth, latitude, longitude }) : getJson("/point/temperature", { forecastHour, depth, latitude, longitude, frame, inputs });
  },
  bottomTemperatureValue({ forecastHour, latitude, longitude, time, frame, inputs }) {
    return time ? getJson("/history/point/bottom-temperature", { time, latitude, longitude })
      : getJson("/point/bottom-temperature", { forecastHour, latitude, longitude, frame, inputs });
  },
  airTemperatureValue({ forecastHour, latitude, longitude, time, frame, inputs }) {
    return time ? getJson("/history/point/air-temperature", { time, latitude, longitude })
      : getJson("/point/air-temperature", { forecastHour, latitude, longitude, frame, inputs });
  },
  profile({ forecastHour, latitude, longitude, time, frame, inputs }) {
    return time ? getJson("/history/point/temperature-profile", { time, latitude, longitude }) : getJson("/point/temperature-profile", { forecastHour, latitude, longitude, frame, inputs });
  },
  currentProfile({ forecastHour, latitude, longitude, time, frame, inputs }) {
    return time ? getJson("/history/point/current-profile", { time, latitude, longitude }) : getJson("/point/current-profile", { forecastHour, latitude, longitude, frame, inputs });
  },
  upwellingValue({ forecastHour, latitude, longitude, time, frame, inputs }) {
    return time ? getJson("/history/point/upwelling", { time, latitude, longitude }) : getJson("/point/upwelling", { forecastHour, latitude, longitude, frame, inputs });
  },
  waveValue({ forecastHour, latitude, longitude, time, frame, inputs }) {
    return time ? getJson("/history/point/waves", { time, latitude, longitude }) : getJson("/point/waves", { forecastHour, latitude, longitude, frame, inputs });
  },
  targetTemperatureValue({ forecastHour, targetC, latitude, longitude }) {
    return getJson("/point/target-temperature", { forecastHour, targetC, latitude, longitude });
  },
  windValue({ forecastHour, latitude, longitude, time, frame, inputs }) {
    return time ? getJson("/history/point/wind", { time, latitude, longitude })
      : getJson("/point/wind", { forecastHour, latitude, longitude, frame, inputs });
  },
  pointForecast({ layer, forecastHour, latitude, longitude, frame, inputs }) {
    return getJson("/point/forecast", { layer, forecastHour, latitude, longitude, frame, inputs });
  },
  depth({ latitude, longitude, signal }) {
    return getJson("/point/depth", { latitude, longitude }, { signal });
  },
  status({ signal } = {}) {
    return getJson("/status", {}, { signal });
  },
  observations({ time, signal } = {}) {
    return time ? getJson("/history/stations", { time }, { signal }) : getJson("/stations", {}, { signal });
  },
  // The saved past: which sample times exist, and one station's last 90 days.
  history({ signal } = {}) {
    return getJson("/history", {}, { signal });
  },
  stationHistory({ id, signal }) {
    return getJson(`/history/stations/${encodeURIComponent(id)}`, {}, { signal });
  },
  modelPoints({ kind, south, west, north, east, signal }) {
    return getJson("/model-points", { kind, south, west, north, east }, { signal });
  }
};

export function setup() {
  window.noaaGreatLakesApi = api;
}
